import classNames from 'classnames';
import React, { useState } from 'react';
import { cn } from '../../common/utils';

export type CounterType = 'route' | 'Warn' | 'Info' | 'Error' | 'Success' | 'Issue';

interface CounterLabelProps {
  children?: React.ReactNode;
  counterSize?: 'xs' | 'sm' | 'lg' | 'xl' | 'date' | 'timedetail' | 'time' | 'issue';
  counterType?: CounterType;
  bgColor?: string;
  textColor?: string;
  className?: string;
  classNameIcon?: string;
  IconItem?: React.ComponentType<{ className: string }>;
  isActive?: boolean;
}

const CounterLabel: React.FC<CounterLabelProps> = ({
  children,
  counterSize = 'sm',
  counterType = 'route',
  bgColor,
  textColor,
  className,
  classNameIcon,
  IconItem,
  isActive = false,
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <span
      aria-label="counterLabel"
      className={classNames(
        'z-50',
        'absolute',
        'flex items-center justify-center',
        'text-center',
        'p-0',
        'text-bold text-xs',
        'rounded-full',
        { 'mx-1 my-2 h-12 w-12 self-start': counterSize === 'xl' },
        { 'mx-1 my-2 h-10 w-10 self-start': counterSize === 'lg' },
        { 'mx-2 my-2 h-8 w-8 self-start': counterSize === 'sm' },
        { 'mx-2 my-2 h-7 w-7 self-start': counterSize === 'xs' },
        { 'mx-2 my-2 h-9 w-9 self-start': counterSize === 'date' },
        { 'mx-2 my-2 mt-6 h-8 w-8 self-start': counterSize === 'time' },
        { 'mx-1 mt-6 h-6 w-12 self-start': counterSize === 'timedetail' },
        { 'mx-3 my-2 h-12 w-6 self-start': counterSize === 'issue' },
        {
          'bg-route-line_full text-menu-active-text':
            !bgColor && counterType === 'route',
        },
        {
          'bg-alerts-warning text-warn-text':
            !bgColor && counterType === 'Warn',
        },
        {
          'bg-alerts-info text-info-text':
            !bgColor && counterType === 'Info',
        },
        {
          'bg-alerts-alert text-warn-text':
            !bgColor && counterType === 'Issue',
        },
        {
          'bg-alerts-error text-error-text':
            !bgColor && counterType === 'Error',
        },
        {
          'bg-alerts-success text-success-text':
            !bgColor && counterType === 'Success',
        },
        className,
      )}
      style={{ background: bgColor, color: textColor }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Animated gradient border */}
      {isActive && (
        <>
          <span
            className="absolute -inset-0.5 rounded-full transition ease-in-out duration-150 animate-spin border-2 border-brand border-dashed"
          >
            <span 
              className="border-2 border-black flex h-full w-full items-center justify-center rounded-full"
            />

              <span 
              className={cn('animate-ping transition-all duration-1000', {
                    'bg-route-line_full': counterType === 'route',
                    'bg-alerts-warning': counterType === 'Warn',
                    'bg-alerts-info': counterType === 'Info',
                    'bg-alerts-alert': counterType === 'Issue',
                    'bg-alerts-error': counterType === 'Error',
                    'bg-alerts-success': counterType === 'Success',
                  })}
            />
          </span>
        </>
      )}
      
      {/* Content */}
      <span className="relative z-10">
        {IconItem && (
          <IconItem
            className={classNames(
              { 'h-6 w-6': counterSize === 'sm' },
              { 'h-4 w-4': counterSize === 'xs' },
              { 'h-3 w-3': counterSize === 'issue' },
              'fill-current',
              classNameIcon,
            )}
          />
        )}
        {children}
      </span>
    </span>
  );
};

export default CounterLabel;
